import { Box, Card, Grid, Skeleton, Stack, styled } from "@mui/material";

const SkeletonCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 16,
}));

const dashboardCardSkeletonWidths = [
  { title: "46%", value: "34%" },
  { title: "64%", value: "52%" },
  { title: "74%", value: "36%" },
  { title: "36%", value: "28%" },
] as const;

export const DashboardCardsSkeleton = () => {
  return (
    <Box pt={2} pb={4}>
      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
        {dashboardCardSkeletonWidths.map((widths, index) => (
          <Grid item lg={3} xs={6} key={index}>
            <SkeletonCard
              sx={{
                p: { xs: "1.1rem", sm: "1.4rem 1.2rem" },
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 0.9, sm: 1.5 }}
                alignItems="center"
                width="100%"
              >
                <Skeleton
                  variant="rounded"
                  width={56}
                  height={56}
                  sx={{ borderRadius: "14px", flexShrink: 0 }}
                />
                <Box
                  width="100%"
                  mt={{ xs: "0.85rem", sm: 0 }}
                  textAlign={{ xs: "center", sm: "left" }}
                >
                  <Skeleton
                    variant="text"
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      width: { xs: "78%", sm: widths.title },
                      mx: { xs: "auto", sm: 0 },
                    }}
                  />
                  <Skeleton
                    variant="text"
                    sx={{
                      fontSize: 18,
                      lineHeight: 1.5,
                      width: { xs: "48%", sm: widths.value },
                      mx: { xs: "auto", sm: 0 },
                    }}
                  />
                </Box>
              </Stack>
            </SkeletonCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export const ListPageSkeleton = () => {
  return (
    <Box pt={2} pb={4}>
      <SkeletonCard>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          mb={2.5}
        >
          <Skeleton variant="rounded" height={48} width={320} />
          <Skeleton variant="rounded" height={46} width={150} />
        </Stack>

        <Skeleton variant="rounded" height={44} width="100%" sx={{ mb: 1.5 }} />

        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            height={52}
            width="100%"
            sx={{ mb: 1.2 }}
          />
        ))}
      </SkeletonCard>
    </Box>
  );
};

export const CalendarPageSkeleton = () => {
  return (
    <Box pt={2} pb={4}>
      <SkeletonCard sx={{ mb: 1.2 }}>
        <Grid container spacing={2}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Grid item sm={4} xs={12} key={index}>
              <Skeleton variant="rounded" height={48} />
            </Grid>
          ))}
        </Grid>
      </SkeletonCard>

      <SkeletonCard sx={{ mb: 1.2 }}>
        <Skeleton variant="rounded" height={48} width="100%" />
      </SkeletonCard>

      <SkeletonCard>
        <Stack direction="row" justifyContent="space-between" mb={2}>
          <Skeleton variant="rounded" height={42} width={180} />
          <Skeleton variant="rounded" height={42} width={130} />
        </Stack>

        <Skeleton variant="rounded" height={480} width="100%" />
      </SkeletonCard>
    </Box>
  );
};

export const ReservationCardsSkeleton = () => {
  return (
    <Box pt={2} pb={4}>
      <Skeleton variant="text" width={280} height={48} sx={{ mb: 2 }} />
      <Grid container spacing={2.5}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Grid item xs={12} md={6} key={index}>
            <SkeletonCard>
              <Skeleton width="60%" height={30} />
              <Skeleton width="30%" height={24} />
              <Skeleton
                variant="rounded"
                width="100%"
                height={2}
                sx={{ my: 1.5 }}
              />
              <Skeleton width="85%" height={22} />
              <Skeleton width="65%" height={22} />
              <Skeleton width="40%" height={22} />
            </SkeletonCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export const GenericCenteredSkeleton = () => {
  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={3}
    >
      <Box width="100%" maxWidth={900}>
        <SkeletonCard>
          <Skeleton variant="text" width="50%" height={42} sx={{ mb: 2 }} />
          <Skeleton
            variant="rounded"
            width="100%"
            height={220}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rounded" height={46} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rounded" height={46} />
            </Grid>
          </Grid>
        </SkeletonCard>
      </Box>
    </Box>
  );
};
